import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { HotkeyModule } from 'angular2-hotkeys';
import { provideToastr } from 'ngx-toastr';

import { environment } from '../environments/environment';

import { appRoutes } from './app.routes';
import { ErrorHandlerService } from './services/error-handler.service';
import { HexPipe } from './shared/hex.pipe';
import { ParameterUnitDisplayPipe } from './shared/parameter-unit-display.pipe';
import { reducers, metaReducers } from './store';
import { AnalogInputsEffects } from './store/analog-inputs.effects';
import { DeviceFileEffects } from './store/device-file.effects';
import { DeviceParameterTraceEffects } from './store/device-parameter-trace.effects';
import { DeviceParameterEffects } from './store/device-parameter.effects';
import { DeviceEffects } from './store/device.effects';
import { ErrorReportEffects } from './store/error-report.effects';
import { FirmwareEffects } from './store/firmware.effects';
import { GlobalEffects } from './store/global.effects';
import { LimitedRangeSystemIdentificationParameterEffects } from './store/limited-range-system-identification-parameter.effects';
import { PlantModelParameterEffects } from './store/plant-model-parameter.effects';
import { PlaygroundEffects } from './store/playground.effects';
import { ReleaseEffects } from './store/release.effects';
import { SignalGeneratorParameterEffects } from './store/signal-generator-parameter.effects';
import { SignalGeneratorEffects } from './store/signal-generator.effects';
import { SmmParameterEffects } from './store/smm-parameter.effects';
import { SystemIdentificationParameterEffects } from './store/system-identification-parameter.effects';
import { TuningControllerParameterEffects } from './store/tuning-controller-parameter.effects';
import { TuningControllerEffects } from './store/tuning-controller.effects';

const storeDevtoolsEnabled = !environment.production;

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(), // Refactor after ng-toaster starts to support angular >v20 animations
    provideRouter(appRoutes),
    provideStore(reducers, {
      metaReducers,
      runtimeChecks: {
        strictActionImmutability: false,
        strictStateImmutability: false,
      },
    }),
    provideRouterStore(),
    storeDevtoolsEnabled
      ? provideStoreDevtools({
          logOnly: false,
          maxAge: 1000,
          connectInZone: true,
        })
      : [],
    provideEffects(
      AnalogInputsEffects,
      DeviceParameterTraceEffects,
      DeviceParameterEffects,
      DeviceFileEffects,
      DeviceEffects,
      ErrorReportEffects,
      FirmwareEffects,
      GlobalEffects,
      LimitedRangeSystemIdentificationParameterEffects,
      PlantModelParameterEffects,
      PlaygroundEffects,
      ReleaseEffects,
      SignalGeneratorParameterEffects,
      SignalGeneratorEffects,
      SmmParameterEffects,
      SystemIdentificationParameterEffects,
      TuningControllerParameterEffects,
      TuningControllerEffects,
    ),
    provideToastr({
      autoDismiss: true,
      maxOpened: 5,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      resetTimeoutOnDuplicate: true,
      timeOut: 5000,
    }),
    ParameterUnitDisplayPipe,
    HexPipe,
    {
      provide: ErrorHandler,
      useExisting: ErrorHandlerService,
    },
    // Modules that do not support Angular standalone
    // concept and need to be migrated or replaced
    importProvidersFrom(
      HotkeyModule.forRoot({
        cheatSheetCloseEsc: true,
        disableCheatSheet: false,
      }),
    ),
  ],
};
